import { GroupedOption, ImageCellRenderer } from '@bestseller/retail-planning.styletimeline';
import { ColDef, IAggFuncParams, ValueFormatterParams } from 'ag-grid-community';
import { sum } from 'ramda';
import { advancedTextColumnFilterOptions } from 'src/components/molecules/AdvancedTextColumnFilter/AdvancedTextColumnFilter';
import { DiscountCellEditor } from 'src/components/molecules/DiscountCellEditor/DiscountCellEditor';
import { EditableValueCell } from 'src/components/molecules/EditableCell/EditableCell';
import { SelectCellEditor } from 'src/components/molecules/SelectCellEditor/SelectCellEditor';
import { SalesCampaignOverviewRow } from 'src/domain/table/sales-campaign-overview.row';
import { ExtendedSalesCampaignDiscountType } from 'src/infrastructure/rest-api/api-types';
import { formatNumber, parseNumber } from 'src/utils/formatNumber';
import { calculateDiscountedSalesPrice } from 'src/utils/gross-margin/calculateGrossMargin';
import { safeRound } from 'src/utils/math';
import { numberWithSpaces } from 'src/utils/numberWithSpaces';
import {
    applySalesCampaignDiscountCellValue,
    changeSalesCampaignDiscountType,
    parsePercentageDiscountInput,
    parseSalesCampaignDiscountCellInput,
    SalesCampaignDiscountCellValue,
} from 'src/utils/salesCampaignDiscount';
import { columns as styleTimelineColumns } from '../../StyleTimeline/data/columns';

const formatDiscountValue = (value: SalesCampaignDiscountCellValue | undefined) => {
    if (!value?.discountType) {
        return '';
    }

    if (value.discountType === 'PERCENTAGE') {
        const decimalCount = Number.isInteger(value.discountValue) ? 0 : 2;
        return typeof value.discountValue === 'number' ? `${formatNumber(value.discountValue, decimalCount)}%` : '';
    }

    if (value.discountType === 'FIXED_PRICE') {
        return value.y ? formatNumber(value.y) : '';
    }

    if (value.discountType === 'BUY_X_GET_Y_PERCENT_OFF_Z') {
        if (typeof value.x !== 'number' || value.y === undefined || value.y === null || typeof value.z !== 'number') {
            return '';
        }
        return `Buy ${value.x}, Get ${formatNumber(value.y, 0)}% Off ${value.z}`;
    }

    if (typeof value.x !== 'number' || !value?.y) {
        return '';
    }

    const formattedY = formatNumber(value.y, 0);
    return `${value.x} for ${formattedY}`;
};

export const defaultColDef: ColDef = {
    resizable: true,
    sortable: true,
    ...advancedTextColumnFilterOptions,
    menuTabs: ['filterMenuTab', 'generalMenuTab'],
    filterValueGetter(params) {
        const value = params.getValue(params.colDef.field ?? '') ?? '';
        const valueFormatter = (params.colDef as ColDef).valueFormatter;
        if (typeof valueFormatter === 'function') {
            return valueFormatter({ ...params, value } as ValueFormatterParams);
        }
        return value;
    },
    tooltipValueGetter(params) {
        return params.valueFormatted ?? params.value;
    },
    enablePivot: false,
    enableValue: false,
    enableRowGroup: false,
};

const salesCampaignPlanningcolumns: ColDef<SalesCampaignOverviewRow>[] = [
    {
        field: 'styleOptionPicture',
        headerName: 'Picture',
        cellRenderer: ImageCellRenderer,
        tooltipValueGetter: () => '',
        suppressFiltersToolPanel: true,
        suppressHeaderMenuButton: true,
        initialWidth: 80,
    },
    {
        field: 'styleName',
        headerName: 'Style Name',
        initialWidth: 400,
    },
    {
        field: 'stockQuantity',
        headerName: 'Stock Quantity',
        initialWidth: 160,
        valueFormatter({ value }) {
            return numberWithSpaces(value) ?? 0;
        },
        aggFunc: (params: IAggFuncParams) => sum(params.values.map(parseNumber)),
    },
    {
        field: 'salesQuantityPast8Weeks',
        headerName: 'Sales QTY P8W',
        initialWidth: 160,
        valueFormatter({ value }) {
            return numberWithSpaces(value) ?? '';
        },
        aggFunc: (params: IAggFuncParams) => sum(params.values.map(parseNumber)),
    },
    {
        field: 'colorName',
        headerName: 'Color',
        initialWidth: 150,
    },
    {
        field: 'variantName',
        headerName: 'Variant',
        initialWidth: 180,
    },
    {
        field: 'recommendedRetailPriceLcy',
        headerName: 'Recommended Retail Price',
        headerTooltip: 'Recommended Retail Price',
        initialWidth: 240,
        valueFormatter({ value, node }) {
            if (node?.footer) return numberWithSpaces(value);
            return formatNumber(value) ?? '';
        },
        aggFunc(params) {
            const values = params.values.map((value) => {
                return value ?? 0;
            });
            return sum(values.map(parseNumber));
        },
    },
    {
        field: 'discount.discountType',
        headerName: 'Discount Type',
        initialWidth: 200,
        editable: true,
        cellRenderer: EditableValueCell,
        cellEditor: SelectCellEditor,
        cellEditorParams: {
            selectOptions: [
                {
                    display: 'Percentage',
                    value: 'PERCENTAGE',
                    isHeader: false,
                },
                {
                    display: 'Fixed price',
                    value: 'FIXED_PRICE',
                    isHeader: false,
                },
                {
                    display: 'X for fixed price',
                    value: 'X_FOR_FIXED_PRICE',
                    isHeader: false,
                },
                {
                    display: 'X for Y',
                    value: 'X_FOR_Y',
                    isHeader: false,
                },
                {
                    display: 'Buy X, Get Y% Off Z',
                    value: 'BUY_X_GET_Y_PERCENT_OFF_Z',
                    isHeader: false,
                },
            ],
        },
        valueFormatter({ value }) {
            switch (value) {
                case 'FIXED_PRICE':
                    return 'Fixed price';
                case 'PERCENTAGE':
                    return 'Percentage';
                case 'X_FOR_FIXED_PRICE':
                    return 'X for fixed price';
                case 'X_FOR_Y':
                    return 'X for Y';
                case 'BUY_X_GET_Y_PERCENT_OFF_Z':
                    return 'Buy X, Get Y% Off Z';
                default:
                    return value;
            }
        },
        valueSetter(params) {
            const option = params.colDef.cellEditorParams?.selectOptions?.find(
                (selectOption: GroupedOption) => selectOption.display === params.newValue
            );
            const newDiscountType = (option ? option.value : params.newValue) as ExtendedSalesCampaignDiscountType;

            params.data.discount = changeSalesCampaignDiscountType(params.data, newDiscountType);
            return true;
        },
    },
    {
        field: 'discount',
        headerName: 'Discount',
        valueGetter(params) {
            if (!params.data) return;
            const discountType = params.data.discount?.discountType;
            const y =
                discountType === 'PERCENTAGE'
                    ? calculateDiscountedSalesPrice(params.data, params.data.discount)
                    : params.data.discount?.valueY;
            const decimalCount = ['FIXED_PRICE', 'PERCENTAGE'].includes(discountType ?? '') ? 2 : 0;
            const formattedY = typeof y === 'number' ? formatNumber(y, decimalCount) : y;
            return {
                x: params.data.discount?.valueX,
                y: formattedY ?? undefined,
                z: params.data.discount?.valueZ,
                discountValue: discountType === 'PERCENTAGE' ? params.data.discount.valueY : undefined,
                discountType: params.data.discount?.discountType,
            };
        },
        valueSetter(params) {
            params.data.discount = applySalesCampaignDiscountCellValue(params.data, params.newValue);
            return true;
        },
        valueFormatter({ value }) {
            return formatDiscountValue(value);
        },
        valueParser({ newValue }) {
            if (typeof newValue !== 'string') {
                return newValue;
            }
            return parseSalesCampaignDiscountCellInput(newValue);
        },
        initialWidth: 200,
        tooltipValueGetter(params) {
            const valueFormatter = (params.colDef as ColDef).valueFormatter;
            if (typeof valueFormatter === 'function') {
                return valueFormatter(params as ValueFormatterParams);
            }
            return params.value;
        },
        editable: true,
        cellRenderer: EditableValueCell,
        cellEditor: DiscountCellEditor,
    },
    {
        field: 'discountPercentage',
        cellDataType: 'number',
        headerName: '% Discount',
        initialWidth: 160,
        editable({ data }) {
            return typeof data?.recommendedRetailPriceLcy === 'number';
        },
        cellRenderer: EditableValueCell,
        valueGetter({ data }) {
            if (data?.discount?.discountType === 'PERCENTAGE' && typeof data.discount.valueY === 'number') {
                return data.discount.valueY;
            }
            if (typeof data?.discountPercentage !== 'number') return null;
            return safeRound(data.discountPercentage);
        },
        valueSetter({ data, newValue }) {
            if (!data) return false;

            const percentage = parsePercentageDiscountInput(newValue) ?? parseNumber(newValue);
            data.discount = {
                ...data.discount,
                discountType: 'PERCENTAGE',
                valueX: 1,
                valueY: Number.isNaN(percentage) ? null : percentage,
                valueZ: null,
            };
            return true;
        },
        valueFormatter({ value, data }) {
            if (!data || typeof data.discount?.valueX !== 'number' || typeof data.discount?.valueY !== 'number') {
                return '';
            }
            const formatted = formatNumber(value, 1);
            return formatted ? `${formatted}%` : '';
        },
    },
    {
        field: 'newSalesPrice',
        headerName: 'New Sales Price',
        initialWidth: 170,
        valueFormatter: (params) => {
            if (
                (!params.data ||
                    typeof params.data.discount?.valueX !== 'number' ||
                    typeof params.data.discount?.valueY !== 'number') &&
                !params.node?.footer // Total row doesn't have data nor discount so gotta exclude it to format it correctly
            ) {
                return '';
            }
            if (params.node?.footer) return numberWithSpaces(params.value);
            return formatNumber(params.value) ?? '';
        },
        aggFunc: (params: IAggFuncParams) => {
            const values = params.values.filter((value) => typeof value === 'number');
            return sum(values);
        },
    },
    {
        field: 'newGMPercent',
        headerName: 'New GM %',
        initialWidth: 160,
        valueFormatter: (params) => {
            const formatted = formatNumber(params.value, 2);
            return formatted ? `${formatted}%` : '';
        },
    },
    {
        field: 'discount.comment',
        headerName: 'Comment for stores',
        headerTooltip: 'This comment shows in the sales campaign PDF',
        initialWidth: 200,
        editable: true,
        cellRenderer: EditableValueCell,
    },
];

export const columns = [
    ...salesCampaignPlanningcolumns,
    // Include all style timeline columns that are not already included in salesCampaignPlanningcolumns, except for collections - but make them hidden by default and non-editable
    ...styleTimelineColumns
        .filter(
            (column) =>
                column.field !== 'collections' &&
                !salesCampaignPlanningcolumns.find((planningCol) => planningCol.field === column.field)
        )
        .map((column) => ({
            ...column,
            editable: false,
            cellRenderer: column.cellRenderer === EditableValueCell ? undefined : column.cellRenderer,
            hide: true,
            rowGroup: false,
            enableRowGroup: false,
            suppressColumnsToolPanel: false,
        })),
];
